/**
 * Hooks for listing source portal entities available for copy.
 */
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type { SourceAgent, SourceCmsPage, SourceFile, SourceLlmModel } from "../types";

export const COPY_QUERY_KEYS = {
  sourceAgents: (srcId: string) => ["portal-copy", "source", "agents", srcId] as const,
  sourceCmsPages: (srcId: string) =>
    ["portal-copy", "source", "cms-pages", srcId] as const,
  sourceFiles: (srcId: string) => ["portal-copy", "source", "files", srcId] as const,
  sourceLlmModels: (srcId: string) =>
    ["portal-copy", "source", "llm-models", srcId] as const,
  alreadyCopied: (destId: string, srcId: string, entityType: string) =>
    ["portal-copy", "already-copied", destId, srcId, entityType] as const,
  history: (destId: string) => ["portal-copy", "history", destId] as const,
};

export function useSourceAgents(sourcePortalId: string | null | undefined) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.sourceAgents(sourcePortalId ?? ""),
    queryFn: async () => {
      const resp = await apiClient.get<SourceAgent[]>(
        API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.SOURCE_AGENTS(sourcePortalId!),
      );
      return resp.data;
    },
    enabled: !!sourcePortalId,
  });
}

export function useSourceCmsPages(sourcePortalId: string | null | undefined) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.sourceCmsPages(sourcePortalId ?? ""),
    queryFn: async () => {
      const resp = await apiClient.get<SourceCmsPage[]>(
        API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.SOURCE_CMS_PAGES(sourcePortalId!),
      );
      return resp.data;
    },
    enabled: !!sourcePortalId,
  });
}

export function useSourceFiles(sourcePortalId: string | null | undefined) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.sourceFiles(sourcePortalId ?? ""),
    queryFn: async () => {
      const resp = await apiClient.get<SourceFile[]>(
        API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.SOURCE_FILES(sourcePortalId!),
      );
      return resp.data;
    },
    enabled: !!sourcePortalId,
  });
}

export function useSourceLlmModels(sourcePortalId: string | null | undefined) {
  return useQuery({
    queryKey: COPY_QUERY_KEYS.sourceLlmModels(sourcePortalId ?? ""),
    queryFn: async () => {
      const resp = await apiClient.get<SourceLlmModel[]>(
        API_ENDPOINTS.SYSTEM.PORTALS.IMPORT.SOURCE_LLM_MODELS(sourcePortalId!),
      );
      return resp.data;
    },
    enabled: !!sourcePortalId,
  });
}
